import { Controller } from "@hotwired/stimulus";
import { application } from "src/stimulus/application";

// Site::SimulatorApartmentCost::Component の Stimulus コントローラ。
// landIeul: components/simulator/ApartmentCost/index.tsx の useState/useEffect を再現する。
// 予算(万円)・土地面積(坪) を入力 → valid 判定でボタン活性 →「事例を検索する」で
// exampleIdMappings により事例ID群を引き当て、GraphQL(GetUseExamplesByIds) で活用事例を取得しカード描画。
// マウント時は id=96 (事例サンプル) を取得 (isDefault=true, オーバーレイで覆う)。

// landIeul: components/simulator/ApartmentCost/exampleIdMapping.ts
interface ExampleIdMapping {
  ids: string[];
  minBudget: number;
  maxBudget: number;
  minArea: number;
  maxArea: number;
}

const exampleIdMappings: ExampleIdMapping[] = [
  { ids: ["26"], minBudget: 0, maxBudget: 5000, minArea: 0, maxArea: 100 },
  { ids: ["83", "152"], minBudget: 0, maxBudget: 5000, minArea: 101, maxArea: 200 },
  { ids: ["143", "145"], minBudget: 0, maxBudget: 5000, minArea: 201, maxArea: 100000 },
  { ids: ["94", "96"], minBudget: 5001, maxBudget: 10000, minArea: 0, maxArea: 100 },
  { ids: ["8", "82", "144"], minBudget: 5001, maxBudget: 10000, minArea: 101, maxArea: 200 },
  { ids: ["41", "54"], minBudget: 5001, maxBudget: 10000, minArea: 201, maxArea: 100000 },
  { ids: ["81", "93"], minBudget: 10001, maxBudget: 100000000, minArea: 0, maxArea: 100 },
  { ids: ["49", "82", "95"], minBudget: 10001, maxBudget: 100000000, minArea: 101, maxArea: 200 },
  { ids: ["56", "91", "92"], minBudget: 10001, maxBudget: 100000000, minArea: 201, maxArea: 100000 },
];

const QUERY = `query GetUseExamplesByIds($ids: [ID!]) {
  useExamples(ids: $ids) {
    nodes { id title brand { id } landArea firstImage { thumbnailUrl } }
  }
}`;

const NO_IMAGE_PATH = "/item/no_image.png";

interface UseExample {
  id: string;
  title: string;
  brand: { id: string };
  landArea: string | null;
  firstImage: { thumbnailUrl: string } | null;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string),
  );
}

class SiteSimulatorApartmentCostController extends Controller {
  static targets = [
    "budget",
    "area",
    "button",
    "cards",
    "budgetLabel",
    "budgetFieldset",
    "budgetLegend",
    "areaLabel",
    "areaFieldset",
    "areaLegend",
  ];
  static values = { graphql: String };

  declare readonly budgetTarget: HTMLInputElement;
  declare readonly areaTarget: HTMLInputElement;
  declare readonly buttonTarget: HTMLButtonElement;
  declare readonly cardsTarget: HTMLElement;
  declare readonly budgetLabelTarget: HTMLElement;
  declare readonly budgetFieldsetTarget: HTMLElement;
  declare readonly budgetLegendTarget: HTMLElement;
  declare readonly areaLabelTarget: HTMLElement;
  declare readonly areaFieldsetTarget: HTMLElement;
  declare readonly areaLegendTarget: HTMLElement;
  declare readonly graphqlValue: string;

  private isDefault = true;

  connect(): void {
    // landIeul: マウント時に id=96 を取得して事例サンプルを表示 (isDefault のまま)。
    void this.fetchExamples(["96"]).then((nodes) => {
      if (this.isDefault && nodes.length > 0) this.renderCards(nodes);
    });
  }

  private parse(input: HTMLInputElement): number | undefined {
    const n = Number(input.value);
    return isNaN(n) ? undefined : n;
  }

  private get budget(): number | undefined {
    return this.parse(this.budgetTarget);
  }

  private get area(): number | undefined {
    return this.parse(this.areaTarget);
  }

  private get valid(): boolean {
    const b = this.budget;
    const a = this.area;
    return !!b && !!a && b > 0 && a > 0;
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.updateFieldState(input, true, input.value !== "");
    this.refreshButton();
  }

  onFocus(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.updateFieldState(input, true, input.value !== "");
  }

  onBlur(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.updateFieldState(input, false, input.value !== "");
  }

  // MUI OutlinedInput の label/notch/border 状態を再現する。
  private updateFieldState(input: HTMLInputElement, focused: boolean, filled: boolean): void {
    const isBudget = input === this.budgetTarget;
    const label = isBudget ? this.budgetLabelTarget : this.areaLabelTarget;
    const fieldset = isBudget ? this.budgetFieldsetTarget : this.areaFieldsetTarget;
    const legend = isBudget ? this.budgetLegendTarget : this.areaLegendTarget;
    const shrink = focused || filled;
    label.classList.toggle("Sim_labelShrink", shrink);
    label.classList.toggle("Sim_labelFocused", focused);
    fieldset.classList.toggle("Sim_fieldsetFocused", focused);
    legend.classList.toggle("Sim_legendOpen", shrink);
  }

  private refreshButton(): void {
    const enabled = this.valid;
    this.buttonTarget.disabled = !enabled;
    this.buttonTarget.classList.toggle("Sim_buttonEnabled", enabled);
    this.buttonTarget.classList.toggle("Sim_buttonDisabled", !enabled);
    this.buttonTarget.tabIndex = enabled ? 0 : -1;
  }

  async search(): Promise<void> {
    const budget = this.budget;
    const area = this.area;
    if (!budget || !area) return;
    const map = exampleIdMappings.find(
      (m) => budget >= m.minBudget && budget <= m.maxBudget && area <= m.maxArea && area >= m.minArea,
    );
    if (!map) return;

    this.showLoading();
    const nodes = await this.fetchExamples(map.ids);
    this.isDefault = false;
    this.renderCards(nodes);
  }

  private async fetchExamples(ids: string[]): Promise<UseExample[]> {
    try {
      const res = await fetch(this.graphqlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: QUERY, variables: { ids } }),
      });
      const json = await res.json();
      return (json?.data?.useExamples?.nodes || []).filter(Boolean) as UseExample[];
    } catch {
      return [];
    }
  }

  // loading 中は CircularProgress のみ表示 (cardsBox を差し替え)。
  private showLoading(): void {
    this.cardsTarget.className = "";
    this.cardsTarget.innerHTML =
      '<span class="Sim_progress"><svg viewBox="22 22 44 44"><circle cx="44" cy="44" r="20.2"></circle></svg></span>';
  }

  private renderCards(nodes: UseExample[]): void {
    this.cardsTarget.className = "Sim_cardsBox";
    if (this.isDefault) this.cardsTarget.classList.add("Sim_cardsBoxDefault");
    if (nodes.length >= 2) this.cardsTarget.classList.add("Sim_cardsBoxStart");
    this.cardsTarget.innerHTML = nodes.map((n) => this.cardHtml(n)).join("");
  }

  private cardHtml(item: UseExample): string {
    const src = item.firstImage?.thumbnailUrl || NO_IMAGE_PATH;
    const title = this.isDefault ? "活用事例サンプル" : item.title;
    const alt = this.isDefault ? "活用事例サンプル" : `${item.title} の施工イメージ`;
    const landArea = this.isDefault ? "？？？" : (item.landArea ?? "");
    const href = this.isDefault ? "#" : `/company/${item.brand.id}/examples/${item.id}/`;
    return `<div class="Sim_card">
      <img class="Sim_cardImg" src="${escapeHtml(src)}" alt="${escapeHtml(alt)}" width="300" height="200" loading="eager">
      <div class="Sim_cardBody">
        <p class="TextBasic_text Sim_cardTitle">${escapeHtml(title)}</p>
        <p class="TextBasic_text">土地面積：${escapeHtml(landArea)}㎡</p>
        <div class="Sim_cardLinkRow">
          <a class="Sim_cardLinkAnchor" href="${escapeHtml(href)}"><p class="TextBasic_text Sim_cardLink">詳しく見る</p></a>
        </div>
      </div>
    </div>`;
  }
}

application.register("site--simulator-apartment-cost", SiteSimulatorApartmentCostController);
